import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Pdf component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconPdf extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M9.86891 15.9803C9.65892 16.619 9.40773 17.1085 9.1232 17.464C8.79438 17.8748 8.36584 18.1683 7.87074 18.189C7.3584 18.2105 6.92953 17.9272 6.72442 17.5178C6.52036 17.1105 6.5512 16.6168 6.82097 16.212L6.82098 16.212C7.16821 15.691 7.79693 15.2854 8.43723 14.9694C8.49903 14.9389 8.56197 14.9088 8.62597 14.879C9.02247 13.3741 9.3491 11.4383 9.45406 9.82821C9.15215 9.36757 8.89248 8.92296 8.69774 8.51619C8.50231 8.10797 8.34444 7.68166 8.30364 7.28667C8.26301 6.89337 8.33003 6.34186 8.83062 6.00511C8.86399 5.98266 8.89912 5.96293 8.93566 5.94611C9.3314 5.76397 9.80468 5.74754 10.2079 6.05244C10.5383 6.30223 10.7009 6.68456 10.7918 6.99633C10.9744 7.6223 11.0127 8.4907 10.978 9.40437C11.1963 9.71819 11.437 10.0442 11.6939 10.375C12.4087 11.2956 13.224 12.2216 13.975 12.9784C14.1171 13.1216 14.2549 13.2567 14.3875 13.3831C14.7597 13.3459 15.1047 13.3261 15.4093 13.3261C16.0472 13.3261 16.6057 13.4596 16.9949 13.7882C17.4265 14.1525 17.5603 14.6893 17.3941 15.1712C17.0818 16.0768 15.9451 16.4167 15.0158 15.798C14.6852 15.612 14.2973 15.3167 13.8847 14.9561C13.0393 15.0785 12.0735 15.283 11.153 15.5503C10.6984 15.6823 10.2643 15.8269 9.86891 15.9803ZM7.76883 16.6962C7.76882 16.6962 7.76931 16.696 7.77029 16.6957ZM12.5512 13.6658C11.958 13.0441 11.3454 12.3447 10.7784 11.6365C10.6625 12.512 10.5052 13.4049 10.3233 14.2341C10.4595 14.1913 10.5968 14.1498 10.7347 14.1098C11.3279 13.9375 11.9464 13.7874 12.5512 13.6658Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5.07267 2.60498C4.62479 2.60498 4.26172 2.96806 4.26172 3.41593V20.584C4.26172 21.0319 4.62478 21.395 5.07267 21.395H18.928C19.3759 21.395 19.739 21.0319 19.739 20.584V8.09651C19.739 7.52663 19.5126 6.98013 19.1098 6.57722L19.104 6.57148L19.1041 6.57145L15.836 3.23162C15.4334 2.83035 14.888 2.60498 14.3194 2.60498H5.07267ZM2.76172 3.41593C2.76172 2.13963 3.79636 1.10498 5.07267 1.10498H14.3194C15.2871 1.10498 16.2151 1.48938 16.8994 2.17362L16.9051 2.17938L16.9051 2.17941L20.1735 5.51962C20.8558 6.20359 21.239 7.1303 21.239 8.09651V20.584C21.239 21.8604 20.2043 22.895 18.928 22.895H5.07267C3.79636 22.895 2.76172 21.8604 2.76172 20.584V3.41593Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-pdf') === undefined) {
  customElements.define('cat-icon-pdf', BlocksIconPdf);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-pdf': BlocksIconPdf;
  }
}
